import axios from 'axios'
import router from '@router'
import {
  getNearestLocations,
  getLocationFromSlug,
  getAllLocations,
} from '@utils/contentful'
import { getBrowserLocation } from '@utils/location'
import config from '@src/config'

export const state = {
  geolocating: null,
  loading: null,
  location: null,
  nearestLocations: [],
  searchLoading: null,
}

export const getters = {
  getGeolocating: (state) => {
    return state?.geolocating
  },
  getLoading: (state) => {
    return state?.loading
  },
  getLocation: (state) => {
    return state?.location
  },
  getNearestLocations: (state) => {
    return state?.nearestLocations
  },
  getSearchLoading: (state) => {
    return state?.searchLoading
  },
  isWantToOrderPromptVisible: (state) => {
    return state.location?.wantToOrderPromptVisible !== false
  },
}

export const mutations = {
  setGeolocating(state, { geolocating }) {
    state.geolocating = geolocating
  },
  setLoading(state, { loading }) {
    state.loading = loading
  },
  setLocation(state, { location }) {
    state.location = location
  },
  setNearestLocations(state, { nearestLocations }) {
    state.nearestLocations = nearestLocations
  },
  setSearchLoading(state, { searchLoading }) {
    state.searchLoading = searchLoading
  },
}

export const actions = {
  clearNearestLocations({ commit }) {
    commit('setNearestLocations', { nearestLocations: [] })
  },

  selectLocation({ commit, dispatch }, { location }) {
    commit('setLocation', { location })
  },

  async setNearestToAll({ commit, dispatch }) {
    try {
      commit('setGeolocating', { geolocating: true })

      const locations = await getAllLocations()

      if (!locations) {
        throw new Error('Could not fetch locations')
      }
      commit('setNearestLocations', {
        nearestLocations: locations,
      })

      commit('setGeolocating', { geolocating: false })
    } catch (error) {
      commit('setGeolocating', { geolocating: false })
      dispatch('clearNearestLocations')
      if (error.message === 'User denied Geolocation') {
        dispatch('ui/showError', 'Geolocation is not enabled for this device', {
          root: true,
        })
      } else {
        dispatch(
          'ui/showError',
          'There was a problem fetching the nearest locations',
          {
            root: true,
          }
        )
      }
    }
  },

  async geolocate({ commit, dispatch }) {
    try {
      commit('setGeolocating', { geolocating: true })

      const coords = await getBrowserLocation()

      if (!coords) {
        throw new Error('Could not get browser location')
      }

      const nearestLocations = await getNearestLocations(coords)

      if (!nearestLocations) {
        throw new Error('Could not fetch locations')
      }

      commit('setNearestLocations', {
        nearestLocations: nearestLocations.filter(
          (x) => x.name !== 'Google Location'
        ),
      })
      commit('setGeolocating', { geolocating: false })
    } catch (error) {
      commit('setGeolocating', { geolocating: false })
      dispatch('clearNearestLocations')
      if (error.message === 'User denied Geolocation') {
        dispatch('ui/showError', 'Geolocation is not enabled for this device', {
          root: true,
        })
      } else {
        dispatch(
          'ui/showError',
          'There was a problem fetching the nearest locations',
          {
            root: true,
          }
        )
      }
    }
  },

  async searchLocation({ commit, dispatch }, { query }) {
    try {
      commit('setSearchLoading', { searchLoading: true })

      const apiKey = config.geolocationKey
      const formattedQuery = `${query.trim().split(' ').join('+')},+uk`
      const url = 'https://maps.googleapis.com/maps/api/geocode/json'

      const res = await axios.get(
        `${url}?address=${formattedQuery}&key=${apiKey}`
      )

      if (
        !res ||
        res.data.error_message === 'The provided API key is invalid.'
      ) {
        throw new Error('Error fetching location coords')
      }

      const coords = res?.data?.results?.[0]?.geometry?.location

      if (!coords || !coords.lat || !coords.lng) {
        throw new Error('Could not find coords')
      }

      const nearestLocations = await getNearestLocations(coords)

      if (!nearestLocations) {
        throw new Error('Could not fetch locations')
      }

      commit('setNearestLocations', {
        nearestLocations: nearestLocations.filter(
          (x) => x.name !== 'Google Location'
        ),
      })
      commit('setSearchLoading', { searchLoading: false })
    } catch (error) {
      commit('setSearchLoading', { searchLoading: false })
      dispatch('clearNearestLocations')
      dispatch(
        'ui/showError',
        error.message === 'Could not find coords'
          ? 'Could not find any locations matching your query'
          : 'There was a problem fetching the nearest locations',
        {
          root: true,
        }
      )
    }
  },

  async getLocationFromSlug({ commit, dispatch }, { slug }) {
    try {
      commit('setLoading', { loading: true })

      const location = await getLocationFromSlug(slug)

      if (!location) {
        throw new Error('Error fetching location')
      }

      commit('setLocation', { location })
      commit('setLoading', { loading: false })
    } catch (error) {
      commit('setLoading', { loading: false })
      dispatch('ui/showError', 'There was a problem fetching the location', {
        root: true,
      })
      router.push('/').catch(() => {})
    }
  },
}
